import { useRef, useEffect, useCallback } from 'react';
import usePersistedState, { clearAllPersistedMapState, applyPendingClear } from './usePersistedState';

// Re-export
export { clearAllPersistedMapState, applyPendingClear };

const getLocalDateStr = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const DEFAULT_ELOGBOOK_SPECIES = ["Arenque", "Anchoveta", "Otros", "Mixto"];

const MAX_MARKERS = 100;
const MAX_PLOTTED_POINTS = 200;
const MAX_ZONE_ANALYSES = 50;
const MAX_MEASUREMENTS = 20;

function cappedSetter(setter, max) {
  return (value) => {
    if (typeof value === 'function') {
      setter((prev) => {
        const next = value(prev);
        return Array.isArray(next) && next.length > max ? next.slice(-max) : next;
      });
    } else {
      setter(Array.isArray(value) && value.length > max ? value.slice(-max) : value);
    }
  };
}

export function usePersistLayerState(mapType, layerState, derivedVarsState) {
  const ref = useRef({ layerState, derivedVarsState });
  ref.current = { layerState, derivedVarsState };

  const storageKey = 'bnf_v1_map_lyr_' + mapType;

  // Restore on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return;
      const saved = JSON.parse(raw);
      if (saved.vis) Object.entries(saved.vis).forEach(([id, v]) => {
        if (v) layerState.setLayerVisible(id, true);
      });
      if (saved.cnt) Object.entries(saved.cnt).forEach(([id, v]) => {
        if (v) layerState.setLayerContoursVisible(id, true);
      });
      if (saved.lvl) Object.entries(saved.lvl).forEach(([id, v]) =>
        layerState.setContourLevelsForLayer(id, v)
      );
      if (saved.clr) Object.entries(saved.clr).forEach(([id, v]) =>
        layerState.setColorRangeForLayer(id, v)
      );
      if (saved.der?.vis) Object.entries(saved.der.vis).forEach(([id, v]) => {
        if (v) derivedVarsState.setVariableVisible(id, true);
      });
      if (saved.der?.clr) Object.entries(saved.der.clr).forEach(([id, v]) =>
        derivedVarsState.setColorRangeForVariable(id, v)
      );
    } catch { /* corrupted data, ignore */ }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist on unmount / beforeunload
  useEffect(() => {
    const save = () => {
      const { layerState: ls, derivedVarsState: dv } = ref.current;
      const data = {
        vis: ls.layerVisibility,
        cnt: ls.layerContours,
        lvl: ls.contourLevels,
        clr: ls.colorRanges,
        der: { vis: dv.visibility, clr: dv.colorRanges },
      };
      try { localStorage.setItem(storageKey, JSON.stringify(data)); } catch { }
    };
    window.addEventListener('beforeunload', save);
    // No guardamos en el cleanup — StrictMode causa doble montaje
    // y el primer unmount guarda estado vacío pisando lo guardado
    return () => { window.removeEventListener('beforeunload', save); };
  }, [mapType, storageKey]);

  // Guardado periódico cada 10s (respaldo por si el navegador crashea)
  useEffect(() => {
    const interval = setInterval(() => {
      const { layerState: ls, derivedVarsState: dv } = ref.current;
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          vis: ls.layerVisibility, cnt: ls.layerContours,
          lvl: ls.contourLevels, clr: ls.colorRanges,
          der: { vis: dv.visibility, clr: dv.colorRanges },
        }));
      } catch { }
    }, 10000);
    return () => clearInterval(interval);
  }, [mapType, storageKey]);
}

export default function usePersistedMapState(mapType = 'main') {
  const prefix = `map_${mapType}_`;

  const [selectedDate, setSelectedDate] = usePersistedState(prefix + 'date', '');
  const [showVmsPositions, setShowVmsPositions] = usePersistedState(prefix + 'vms_pos', false);
  const [showVmsTracks, setShowVmsTracks] = usePersistedState(prefix + 'vms_trk', false);
  const [vmsTrackHours, setVmsTrackHours] = usePersistedState(prefix + 'vms_hrs', 48);

  const [showAisPositions, setShowAisPositions] = usePersistedState(prefix + 'ais_pos', false);
  const [aisTypes, setAisTypes] = usePersistedState(prefix + 'ais_types', []);
  const [aisFlags, setAisFlags] = usePersistedState(prefix + 'ais_flags', []);
  const [aisStatuses, setAisStatuses] = usePersistedState(prefix + 'ais_status', []);
  const [aisNameQuery, setAisNameQuery] = usePersistedState(prefix + 'ais_name', '');

  const [_dropMarkers, _setDropMarkers] = usePersistedState(prefix + 'markers', []);
  const [_plottedPoints, _setPlottedPoints] = usePersistedState(prefix + 'plt_pts', []);
  const setDropMarkers = useCallback((v) => cappedSetter(_setDropMarkers, MAX_MARKERS)(v), [_setDropMarkers]);
  const setPlottedPoints = useCallback((v) => cappedSetter(_setPlottedPoints, MAX_PLOTTED_POINTS)(v), [_setPlottedPoints]);

  const [showElogbookPositions, setShowElogbookPositions] = usePersistedState(prefix + 'elog_vis', false);
  const [elogbookDateFrom, setElogbookDateFrom] = usePersistedState(prefix + 'elog_fr', () => {
    const d = new Date(); d.setDate(d.getDate() - 1);
    return getLocalDateStr(d);
  });
  const [elogbookDateTo, setElogbookDateTo] = usePersistedState(prefix + 'elog_to', () => getLocalDateStr(new Date()));
  const [elogbookActiveSpecies, setElogbookActiveSpecies] = usePersistedState(prefix + 'elog_sp', DEFAULT_ELOGBOOK_SPECIES);
  const [elogbookHasSearched, setElogbookHasSearched] = usePersistedState(prefix + 'elog_done', false);

  const [showGrid, setShowGrid] = usePersistedState(prefix + 'grid', true);
  const [showProtectedAreas, setShowProtectedAreas] = usePersistedState(prefix + 'anp', false);
  const [showIsoParalitorales, setShowIsoParalitorales] = usePersistedState(prefix + 'iso', false);
  const [showEEZ, setShowEEZ] = usePersistedState(prefix + 'eez', false);

  const [activeMeasureTool, setActiveMeasureTool] = usePersistedState(prefix + 'meas_tool', 'none');
  const [_measurements, _setMeasurements] = usePersistedState(prefix + 'measurements', []);
  const setMeasurements = useCallback((v) => cappedSetter(_setMeasurements, MAX_MEASUREMENTS)(v), [_setMeasurements]);

  const [showZonePanel, setShowZonePanel] = usePersistedState(prefix + 'zone_panel', false);
  const [hasZoneAnalyses, setHasZoneAnalyses] = usePersistedState(prefix + 'has_zones', false);

  return {
    selectedDate, setSelectedDate,
    showVmsPositions, setShowVmsPositions,
    showVmsTracks, setShowVmsTracks,
    vmsTrackHours, setVmsTrackHours,
    showAisPositions, setShowAisPositions,
    aisTypes, setAisTypes,
    aisFlags, setAisFlags,
    aisStatuses, setAisStatuses,
    aisNameQuery, setAisNameQuery,
    dropMarkers: _dropMarkers, setDropMarkers,
    plottedPoints: _plottedPoints, setPlottedPoints,
    showElogbookPositions, setShowElogbookPositions,
    elogbookDateFrom, setElogbookDateFrom,
    elogbookDateTo, setElogbookDateTo,
    elogbookActiveSpecies, setElogbookActiveSpecies,
    elogbookHasSearched, setElogbookHasSearched,
    showGrid, setShowGrid,
    showProtectedAreas, setShowProtectedAreas,
    showIsoParalitorales, setShowIsoParalitorales,
    showEEZ, setShowEEZ,
    activeMeasureTool, setActiveMeasureTool,
    measurements: _measurements, setMeasurements,
    showZonePanel, setShowZonePanel,
    hasZoneAnalyses, setHasZoneAnalyses,
  };
}
